import { useState, useEffect, type ReactNode, type FormEvent } from 'react';

/**
 * 管理画面の簡易パスワードゲート。
 *
 * ※ あくまで「簡易」ロックです。パスワードはビルド後のJSに含まれるため、
 *   高度な攻撃者には突破され得ます。casual access（URLを知っただけの第三者）を
 *   防ぐ目的のものです。本格的な保護が必要になったら Supabase Auth 等へ移行します。
 *
 * パスワードの変更方法:
 *   - 環境変数 VITE_ADMIN_PASSWORD を設定（推奨。GitHub Secrets 等）
 *   - 未設定の場合は下の DEFAULT_PASSWORD が使われます
 */
const DEFAULT_PASSWORD = 'safari415';
const ADMIN_PASSWORD =
  (import.meta.env.VITE_ADMIN_PASSWORD as string | undefined) || DEFAULT_PASSWORD;
const STORAGE_KEY = 'lovelark_admin_unlock';

export default function AdminGate({ children }: { children: ReactNode }) {
  const [unlocked, setUnlocked] = useState(false);
  const [input, setInput] = useState('');
  const [error, setError] = useState(false);

  useEffect(() => {
    // 一度解除したら、同じパスワードの間は記憶（パスワード変更で自動失効）
    if (localStorage.getItem(STORAGE_KEY) === ADMIN_PASSWORD) {
      setUnlocked(true);
    }
  }, []);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (input === ADMIN_PASSWORD) {
      localStorage.setItem(STORAGE_KEY, ADMIN_PASSWORD);
      setUnlocked(true);
      setError(false);
    } else {
      setError(true);
    }
  };

  if (unlocked) return <>{children}</>;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-sm bg-white rounded-xl border border-gray-200 shadow-sm p-8"
      >
        <div className="text-center mb-6">
          <div className="text-lg font-bold tracking-tight">415SAFARI</div>
          <div className="text-xs text-gray-400 mt-1">管理画面</div>
        </div>
        <label className="block text-sm font-medium text-gray-700 mb-2">パスワード</label>
        <input
          type="password"
          value={input}
          onChange={(e) => { setInput(e.target.value); setError(false); }}
          autoFocus
          className={`w-full px-3 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2 ${
            error ? 'border-red-300 focus:ring-red-200' : 'border-gray-300 focus:ring-gray-200'
          }`}
          placeholder="パスワードを入力"
        />
        {error && (
          <p className="text-xs text-red-500 mt-2">パスワードが違います</p>
        )}
        <button
          type="submit"
          className="w-full mt-4 bg-gray-900 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors"
        >
          ログイン
        </button>
      </form>
    </div>
  );
}
